import * as React from "react";
import { Card, CardBody, CardHeader, Image, Button, addToast, CardFooter } from "@heroui/react";
import { GridBackground } from "./ui/GridBackground";
import { TypewriterEffectSmooth } from "./ui/TypeWriterEffect";
import useAuthStore from "../store/authStore";
import { useTranslation } from "react-i18next";

const clientId = "LkarFQNeplrfRqIOMzKFwvy0ZrfyK5J0CYeZzQZbuAXs5EnNEjUivOx9ksTymdkj";
const redirectUri = "https://addin.spellcheck.mn/authRedirect.html";
const scopes = "openid phone email profile";
const state = Math.random().toString(36).substring(2);

const AuthPage: React.FC = () => {
  const { login } = useAuthStore();
  const [isWord, setIsWord] = React.useState<boolean | null>(null);
  const { t } = useTranslation();

  React.useEffect(() => {
    // @ts-ignore
    if (window.Office) {
      // @ts-ignore
      const host = Office.context?.host;
      setIsWord(host === Office.HostType.Word);
    } else {
      setIsWord(false);
    }
  }, []);

  const loginWithPopupDialog = async () => {
    if (isWord === false) {
      addToast({ title: "This app works only in Microsoft Word." });
      return;
    }
    try {
      const authUrl =
        `https://todu.tech/auth?access_type=offline&` +
        `redirect_uri=${redirectUri}&` +
        `response_type=code&` +
        `client_id=${clientId}&` +
        `scope=${scopes}&` +
        `state=${state}`;

      Office.context.ui.displayDialogAsync(authUrl, { height: 60, width: 60 }, (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) return;

        const dialog = result.value;

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (arg) => {
          try {
            if ("message" in arg) {
              const tokenInfo = JSON.parse(arg.message);

              if (tokenInfo.code) {
                const tokens = await exchangeCodeForTokens(tokenInfo.code);

                if (tokens) {
                  addToast({ title: t("loginSuccessful") });
                } else {
                  addToast({ title: t("loginFailed") });
                }

                dialog.close();
              } else if (tokenInfo.error) {
                addToast({ title: t("loginFailed") });
                dialog.close();
              }
            }
          } catch (parseError) {
            dialog.close();
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
          if ("error" in arg) {
            console.error("Dialog error:", arg.error);
          }
        });
      });
    } catch (error) {
      console.error("Login popup failed:", error);
    }
  };

  const exchangeCodeForTokens = async (code: string) => {
    try {
      const apiUrl = (window as any).__CONFIG__?.apiUrl;
      const response = await fetch(`${apiUrl}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });

      if (!response.ok) {
        let errorMsg = `HTTP error ${response.status}`;
        try {
          const errData = await response.json();
          errorMsg = errData.message || JSON.stringify(errData);
        } catch (_) {}
        throw new Error(errorMsg);
      }

      const data = await response.json();

      login(data.jwt, data.refreshToken);

      return data;
    } catch (err: any) {
      console.error("Login failed:", err);
      addToast({ title: t("loginFailed") });
      return null;
    }
  };

  const loginTexts = [
    {
      text: t("loginText1"),
      className: "text-2xl",
    },
    {
      text: t("loginText2"),
      className: "text-2xl",
    },
    {
      text: t("loginText3"),
      className: "text-2xl",
    },
  ];

  return (
    <GridBackground>
      <Card className="w-[330px] max-w-md shadow-2xl px-6">
        <CardHeader className="flex flex-col gap-3 pb-2">
          <div className="flex items-center justify-center w-45 h-12 bg-gradient-to-br rounded-full mx-auto">
            <Image
              width={30}
              height={30}
              radius="md"
              shadow="md"
              src="/assets/logo-filled.png"
              alt="Bolor Duran Logo"
            />
            <p className="ml-2 text-md font-bold text-inherit">Bolor Duran</p>
          </div>
          <div className="text-center">
            <TypewriterEffectSmooth className="ml-2" words={loginTexts} />
          </div>
        </CardHeader>

        <CardBody className="gap-2 mb-2">
          <Button
            variant="flat"
            className="group hover:bg-black hover:text-white"
            type="button"
            size="md"
            onPress={loginWithPopupDialog}
          >
            <svg
              className="size-5 group-hover:fill-white"
              width="27"
              height="25"
              viewBox="0 0 27 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.4329 0.296298L3.31253 3.69119C2.74414 3.90387 2.26771 4.30813 1.96535 4.83432C1.66299 5.36052 1.55363 5.97569 1.6561 6.57386L3.43006 16.8761C3.48911 17.216 3.61498 17.5408 3.80037 17.8318C3.98798 18.1223 4.23236 18.372 4.51883 18.5658L11.8541 23.6659C12.2906 23.9692 12.8094 24.1317 13.3409 24.1317C13.8724 24.1317 14.3913 23.9692 14.8277 23.6659L22.1675 18.5658C22.4506 18.3689 22.6919 18.1178 22.8773 17.827C23.0627 17.5362 23.1885 17.2114 23.2474 16.8716L25.0213 6.56942C25.1251 5.97114 25.0163 5.35539 24.7138 4.8289C24.4113 4.3024 23.9341 3.89835 23.3649 3.68676L14.2468 0.296298C13.662 0.0768526 13.0176 0.0768526 12.4329 0.296298Z"
                fill="#020617"
                className="group-hover:fill-white"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M18.2027 7.89762C18.2027 7.73762 18.1396 7.58408 18.0271 7.47033C17.9146 7.35658 17.7618 7.29179 17.6018 7.29004H9.08903C9.00924 7.29004 8.93023 7.30575 8.85651 7.33629C8.7828 7.36682 8.71582 7.41157 8.6594 7.46799C8.60298 7.52441 8.55823 7.59139 8.52769 7.66511C8.49716 7.73882 8.48145 7.81783 8.48145 7.89762V8.84224C8.48251 8.97907 8.52972 9.11152 8.61544 9.21817C8.70116 9.32482 8.82037 9.39941 8.95376 9.42986L11.3109 10.0197C11.4358 10.0536 11.5467 10.1266 11.6272 10.2279C11.7078 10.3292 11.7539 10.4536 11.7588 10.5829L12.0937 16.244C12.1022 16.3988 12.1697 16.5443 12.2823 16.6508C12.3949 16.7572 12.5441 16.8164 12.699 16.8161H13.9829C14.1381 16.8164 14.2875 16.7573 14.4005 16.6509C14.5135 16.5445 14.5814 16.399 14.5905 16.244L14.9253 10.5829C14.9306 10.452 14.9782 10.3264 15.061 10.2248C15.1437 10.1232 15.2572 10.0513 15.3844 10.0197L17.7415 9.42986C17.8728 9.39727 17.9895 9.32176 18.0731 9.21531C18.1566 9.10886 18.2023 8.97757 18.2027 8.84224V7.89762Z"
                fill="white"
                className="group-hover:fill-black"
              />
            </svg>
            <span className="ml-2">{t("loginWithTodu")}</span>
          </Button>
        </CardBody>
        <CardFooter className="flex flex-col justify-center items-center text-sm opacity-70">
          <p>@{new Date().getFullYear()} Bolorsoft LLC</p>
          <p>v1.0.0</p>
        </CardFooter>
      </Card>
    </GridBackground>
  );
};

export default AuthPage;
